/**
 * Billing account rail content — account identity, active-agreement
 * management, and notes. Shared between the desktop rail and the mobile
 * collapsible summary (BillingAccountLayout / AccountMobileSummary); this
 * component owns only the sections themselves, not outer sizing, scroll, or
 * chrome, so each shell can size and contain it differently.
 *
 * Full agreement management (create/edit, older agreements, notes) lives
 * here so it's always in view beside whichever subview the account tabs
 * show, using AgreementCard's compact mode to fit the narrow column.
 *
 * Contains:
 * - BillingAccountRail — rail content (default export)
 */
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import StatusChip from '@sophocles/components/StatusChip';
import { selectBillingAgreementsForAccount } from '@sophocles/features/billing/slice';
import { AgreementCard, AgreementDialog } from '@sophocles/views/billing/components';
import AccountBillingNotes from '@sophocles/views/billing/components/AccountBillingNotes';

function BillingAccountRail({ account, hideIdentity = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = useParams();
  const { permissions: isQuickBooksUser } = useUser();

  const agreements = useSelector(selectBillingAgreementsForAccount(accountId));
  const activeAgreement = agreements.length > 0 ? agreements[0] : null;
  const olderAgreements = agreements.filter(agreement => !agreement.is_active);

  const [showOlderAgreements, setShowOlderAgreements] = useState(false);
  const [selectedAgreementId, setSelectedAgreementId] = useState(null);
  const [isAgreementDialogOpen, setIsAgreementDialogOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {!hideIdentity && (
        <>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
                {account?.customer?.name}
              </Typography>
              {account?.customer?.id && (
                <Tooltip title={t('billing.account.viewCustomer')}>
                  <IconButton size="small" onClick={() => navigate(`/c/${account.customer.id}/plants`)}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <StatusChip color="ok" label={t('billing.prepaidCoverage.statusActive')} />
            </Stack>
          </Stack>

          <Divider />
        </>
      )}

      <AgreementCard
        agreement={activeAgreement}
        compact
        onEdit={id => {
          setSelectedAgreementId(id);
          setIsAgreementDialogOpen(true);
        }}
        onShowHistory={olderAgreements.length > 0 ? () => setShowOlderAgreements(true) : undefined}
        historyCount={olderAgreements.length}
      />

      <Divider />

      <AccountBillingNotes accountId={account?.id} />

      <Dialog open={showOlderAgreements} onClose={() => setShowOlderAgreements(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.account.olderAgreements', 'Older Agreements')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {olderAgreements.map(agreement => (
              <AgreementCard key={agreement.id} agreement={agreement} />
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      <AgreementDialog
        id={selectedAgreementId}
        accountId={account?.id}
        open={isAgreementDialogOpen}
        onClose={() => setIsAgreementDialogOpen(!isAgreementDialogOpen)}
      />
    </Box>
  );
}

export default BillingAccountRail;
